/**
 * Authentication Utilities
 * 
 * Utility functions for working with authentication and user information
 * in components that need current user data.
 */

import authService from '../services/auth.service';

/**
 * Get current user information from authentication service
 * @returns {Object} Current user information
 */
export const getCurrentUser = () => {
    try {
        const userInfo = authService.getUserInfo();
        
        if (userInfo) {
            return {
                id: userInfo.id || userInfo.userId,
                name: userInfo.name || userInfo.full_name || userInfo.username,
                email: userInfo.email,
                role: userInfo.role || userInfo.user_role, // Legacy support
                roles: userInfo.roles || (userInfo.role ? [userInfo.role] : []), // Prefer roles array
                title: userInfo.title || userInfo.professional_title || 'Dr.',
                fullName: userInfo.full_name || userInfo.name,
                firstName: userInfo.first_name || userInfo.firstName,
                lastName: userInfo.last_name || userInfo.lastName,
                ...userInfo
            };
        }
        
        return null;
    } catch (error) {
        console.error('Error getting current user:', error);
        return null;
    }
};

/**
 * Get current physician name for display
 * @returns {string} Formatted physician name
 */
export const getCurrentPhysicianName = () => {
    const user = getCurrentUser();
    
    if (user) {
        // Try different name formats
        if (user.fullName) {
            return user.fullName;
        }
        
        if (user.firstName && user.lastName) {
            return `${user.firstName} ${user.lastName}`;
        }
        
        if (user.name) {
            return user.name;
        }
        
        if (user.username) {
            return user.username;
        }
    }
    
    // Fallback to generic name
    return 'Current Physician';
};

/**
 * Get current physician name with title for formal display
 * @returns {string} Formatted physician name with title
 */
export const getCurrentPhysicianDisplayName = () => {
    const user = getCurrentUser();
    
    if (user) {
        const title = user.title || 'Dr.';
        const name = getCurrentPhysicianName();
        
        // If name already includes title, don't add it again
        if (name.toLowerCase().includes('dr.') || name.toLowerCase().includes('doctor')) {
            return name;
        }
        
        return `${title} ${name}`;
    }
    
    return 'Dr. Current Physician';
};

/**
 * Get current user ID for backend operations
 * @returns {string|null} Current user ID
 */
export const getCurrentUserId = () => {
    const user = getCurrentUser();
    return user ? (user.id || user.userId) : null;
};

/**
 * Check if user is authenticated
 * @returns {boolean} Authentication status
 */
export const isUserAuthenticated = () => {
    return authService.isAuthenticated();
};

/**
 * Get current user's role (legacy - returns first role)
 * @returns {string|null} Current user role
 */
export const getCurrentUserRole = () => {
    const user = getCurrentUser();
    if (user?.roles && Array.isArray(user.roles) && user.roles.length > 0) {
        return user.roles[0]; // Return first role for legacy compatibility
    }
    return user ? user.role : null;
};

/**
 * Get current user's roles array
 * @returns {Array<string>} Current user roles
 */
export const getCurrentUserRoles = () => {
    const user = getCurrentUser();
    return user?.roles || [];
};

/**
 * Check if current user has a specific role
 * @param {string} roleToCheck - Role to check for
 * @returns {boolean} Whether user has the role
 */
export const userHasRole = (roleToCheck) => {
    const user = getCurrentUser();
    if (user?.roles && Array.isArray(user.roles)) {
        return user.roles.includes(roleToCheck);
    }
    // Fallback to legacy role check
    return user?.role === roleToCheck;
};

/**
 * Get current user's name (alias for getCurrentPhysicianName)
 * @returns {string} Current user name
 */
export const getCurrentUserName = () => {
    return getCurrentPhysicianName();
};

/**
 * React hook to get current user information
 * This provides reactive updates when user info changes
 */
export const useCurrentUser = () => {
    // This is a simple implementation - in a full React context setup,
    // this would be more sophisticated with state management
    return getCurrentUser();
};

export default {
    getCurrentUser,
    getCurrentPhysicianName,
    getCurrentPhysicianDisplayName,
    getCurrentUserId,
    isUserAuthenticated,
    getCurrentUserRole,
    getCurrentUserRoles,
    userHasRole,
    getCurrentUserName,
    useCurrentUser
};